/**
 * Minimal dependency-free ZIP writer (STORED/uncompressed entries only).
 * Enough to produce a valid .xlsx (which is just a ZIP of XML parts) without
 * pulling in a compression library — STORED entries don't need one, they're
 * legal per the ZIP spec and every reader (Excel, 7zip, unzip) accepts them.
 */

const CRC_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) {
            c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        }
        table[n] = c >>> 0;
    }
    return table;
})();

function crc32(bytes) {
    let crc = 0xffffffff;
    for (let i = 0; i < bytes.length; i++) {
        crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
    }
    return (crc ^ 0xffffffff) >>> 0;
}

function toUtf8Bytes(str) {
    return new TextEncoder().encode(str);
}

function writeUint32LE(view, offset, value) {
    view.setUint32(offset, value, true);
}
function writeUint16LE(view, offset, value) {
    view.setUint16(offset, value, true);
}

/** entries: [{ name: string, content: string }] — content is UTF-8 text. */
export function createZip(entries) {
    const parts = [];
    const centralDirEntries = [];
    let offset = 0;

    entries.forEach(({ name, content }) => {
        const nameBytes = toUtf8Bytes(name);
        const dataBytes = toUtf8Bytes(content);
        const crc = crc32(dataBytes);

        const localHeader = new ArrayBuffer(30);
        const lv = new DataView(localHeader);
        writeUint32LE(lv, 0, 0x04034b50);
        writeUint16LE(lv, 4, 20); // version needed
        writeUint16LE(lv, 6, 0); // flags
        writeUint16LE(lv, 8, 0); // stored
        writeUint16LE(lv, 10, 0); // mod time
        writeUint16LE(lv, 12, 0x21); // mod date (arbitrary valid value)
        writeUint32LE(lv, 14, crc);
        writeUint32LE(lv, 18, dataBytes.length); // compressed size
        writeUint32LE(lv, 22, dataBytes.length); // uncompressed size
        writeUint16LE(lv, 26, nameBytes.length);
        writeUint16LE(lv, 28, 0); // extra field length

        parts.push(new Uint8Array(localHeader), nameBytes, dataBytes);

        const localOffset = offset;
        offset += 30 + nameBytes.length + dataBytes.length;

        const centralHeader = new ArrayBuffer(46);
        const cv = new DataView(centralHeader);
        writeUint32LE(cv, 0, 0x02014b50);
        writeUint16LE(cv, 4, 20); // version made by
        writeUint16LE(cv, 6, 20); // version needed
        writeUint16LE(cv, 8, 0); // flags
        writeUint16LE(cv, 10, 0); // stored
        writeUint16LE(cv, 12, 0); // mod time
        writeUint16LE(cv, 14, 0x21); // mod date
        writeUint32LE(cv, 16, crc);
        writeUint32LE(cv, 20, dataBytes.length);
        writeUint32LE(cv, 24, dataBytes.length);
        writeUint16LE(cv, 28, nameBytes.length);
        writeUint16LE(cv, 30, 0); // extra length
        writeUint16LE(cv, 32, 0); // comment length
        writeUint16LE(cv, 34, 0); // disk number
        writeUint16LE(cv, 36, 0); // internal attrs
        writeUint32LE(cv, 38, 0); // external attrs
        writeUint32LE(cv, 42, localOffset);

        centralDirEntries.push(new Uint8Array(centralHeader), nameBytes);
    });

    const centralDirStart = offset;
    let centralDirSize = 0;
    centralDirEntries.forEach((chunk) => { centralDirSize += chunk.length; });

    const eocd = new ArrayBuffer(22);
    const ev = new DataView(eocd);
    writeUint32LE(ev, 0, 0x06054b50);
    writeUint16LE(ev, 4, 0); // disk number
    writeUint16LE(ev, 6, 0); // disk with central dir
    writeUint16LE(ev, 8, entries.length);
    writeUint16LE(ev, 10, entries.length);
    writeUint32LE(ev, 12, centralDirSize);
    writeUint32LE(ev, 16, centralDirStart);
    writeUint16LE(ev, 20, 0); // comment length

    const allParts = [...parts, ...centralDirEntries, new Uint8Array(eocd)];
    return new Blob(allParts, { type: 'application/zip' });
}
